import { Card, CardContent } from "@/components/ui/card";
import {
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from "@/components/ui/form";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

interface Props {
	name: string;
	label: string;
}

interface TreatmentState {
	hasTried: boolean | null;
	duration: string;
	isOngoing: "yes" | "no";
}

const durationOptions = [
	{ label: "Less than 1 month", value: "less_than_1_month" },
	{ label: "1 to 3 months", value: "1_to_3_months" },
	{ label: "3 to 6 months", value: "3_to_6_months" },
	{ label: "6 to 12 months", value: "6_to_12_months" },
	{ label: "More than 12 months", value: "more_than_12_months" },
];

const TriedTreatment = ({ name, label }: Props) => {
	const { control, setValue, getValues } = useFormContext();
	const [state, setState] = useState<TreatmentState>({
		hasTried: null,
		duration: "",
		isOngoing: "no",
	});

	const updateState = (updates: Partial<TreatmentState>) => {
		const newState = { ...state, ...updates };
		setState(newState);

		// Update form values - set the entire object at once
		setValue(name, {
			hasTried: newState.hasTried,
			duration: newState.duration,
			isOngoing: newState.isOngoing,
		});
	};

	return (
		<Card>
			<CardContent className="p-4">
				<FormField
					control={control}
					name={`${name}.hasTried`}
					render={() => (
						<FormItem className="space-y-6">
							<FormLabel className="font-normal text-base">
								Have you tried {label}?
							</FormLabel>
							<FormControl>
								<RadioGroup
									value={
										state.hasTried === null ? "" : state.hasTried ? "yes" : "no"
									}
									onValueChange={(value) => {
										updateState({ hasTried: value === "yes" });
									}}
									className="flex flex-row space-x-4"
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="yes" id={`${name}-tried-yes`} />
										<Label htmlFor={`${name}-tried-yes`}>Yes</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="no" id={`${name}-tried-no`} />
										<Label htmlFor={`${name}-tried-no`}>No</Label>
									</div>
								</RadioGroup>
							</FormControl>
						</FormItem>
					)}
				/>

				{state.hasTried === true && (
					<>
						<FormField
							control={control}
							name={`${name}.duration`}
							render={() => (
								<FormItem className="mt-4">
									<FormLabel>I tried it for</FormLabel>
									<Select
										value={state.duration}
										onValueChange={(value) => updateState({ duration: value })}
									>
										<FormControl>
											<SelectTrigger>
												<SelectValue placeholder="Select duration" />
											</SelectTrigger>
										</FormControl>
										<SelectContent>
											{durationOptions.map((option) => (
												<SelectItem key={option.value} value={option.value}>
													{option.label}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FormMessage />
								</FormItem>
							)}
						/>

						<FormField
							control={control}
							name={`${name}.isOngoing`}
							render={() => (
								<FormItem className="mt-4">
									<FormLabel>Are you still trying {label}?</FormLabel>
									<FormControl>
										<RadioGroup
											value={state.isOngoing}
											onValueChange={(value: "yes" | "no") =>
												updateState({ isOngoing: value })
											}
											className="flex flex-row space-x-4"
										>
											<div className="flex items-center space-x-2">
												<RadioGroupItem value="yes" id={`${name}-yes`} />
												<Label htmlFor={`${name}-yes`}>Yes</Label>
											</div>
											<div className="flex items-center space-x-2">
												<RadioGroupItem value="no" id={`${name}-no`} />
												<Label htmlFor={`${name}-no`}>No</Label>
											</div>
										</RadioGroup>
									</FormControl>
									<FormMessage />
								</FormItem>
							)}
						/>
					</>
				)}
			</CardContent>
		</Card>
	);
};

export default TriedTreatment;
